import { useContext, useState } from 'react';
import { Box, Chip, Container, IconButton, Paper } from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { Autorenew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFullDialog,
    SttTable,
    SttTopControls,
} from '../../../sporttia/all';
import {
    SttChipDeviceType,
    DeviceType,
} from '../../../sporttia/chips/SttChipDeviceType';

const moduleColors: Record<string, string> = {
    BONO: '#269c97',
    BOOKING: '#ff5353',
    ACTIVITY: '#fd66bc',
    GROUP: '#8a3930',
};

type SttChipModuleProps = {
    module: string;
};

function SttChipModule({ module }: SttChipModuleProps): React.ReactElement {
    const { t } = useTranslation();

    const getModuleDisplayName = (): string => {
        switch (module) {
            case 'BONO':
                return t('Bono');
            case 'BOOKING':
                return t('Booking');
            case 'ACTIVITY':
                return t('Activity');
            case 'GROUP':
                return t('Group');
            default:
                return module;
        }
    };

    const label = getModuleDisplayName();
    const color = moduleColors[module] || 'default';

    return (
        <Chip
            size="small"
            label={label}
            style={{ backgroundColor: color, color: 'white' }}
        />
    );
}

type Props = {
    openStatus?: boolean;
    onClose: () => void;
};

type Filters = {
    deviceId: string;
    unlockerCode: string;
    mshipFullname: string;
};

type Authorization = {
    deviceId: string;
    deviceName: string;
    deviceType: string;
    module: string;
    ini: string;
    end: string;
    unlockerCode: string;
    mshipFullname: string;
};

export function DevicesAuthorizationsDialog({
    openStatus = false,
    onClose,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    const [authorizations, setAuthorizations] = useState<{
        data: Authorization[];
        total: number;
    } | null>(null);

    const [filters, setFilter] = useState<Filters>({
        deviceId: '',
        unlockerCode: '',
        mshipFullname: '',
    });

    /**
     * Load authorizations
     */
    const loadAuthorizations = (p = { page: 1, rows: 30 }): void => {
        const finalFilters: Filters = {
            deviceId: filters.deviceId,
            unlockerCode: filters.unlockerCode,
            mshipFullname: filters.mshipFullname,
        };

        cxt?.api('GET', `/scs/${cxt?.sc?.id}/devices/single-authorizations`, {
            params: {
                page: p.page,
                rows: p.rows,
                ...finalFilters,
            },
            success: (r: { data: Authorization[]; total: number }) => {
                setAuthorizations(r);
            },
        });
    };

    const createSingleAuthorizations = (): void => {
        cxt?.api('POST', `/scs/${cxt?.sc?.id}/devices/single-authorizations`, {
            success: () => {
                cxt.showMessage('S', t('AuthorizationsRefreshedSuccessfully'));
                loadAuthorizations();
            },
        });
    };

    return (
        <SttFullDialog
            open={openStatus}
            onClose={onClose}
            title={t('Authorizations')}
        >
            <Container>
                <Box my={3}>
                    <Paper>
                        <SttTopControls
                            p={2}
                            mainAction={{
                                type: 'component',
                                component: (
                                    <IconButton
                                        onClick={() => loadAuthorizations()}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                ),
                            }}
                            menu={[
                                {
                                    caption: t('RefreshAuthorizations'),
                                    icon: <Autorenew />,
                                    onClick: () => createSingleAuthorizations(),
                                },
                            ]}
                            fields={[
                                {
                                    caption: t('DeviceId'),
                                    type: 'text',
                                    name: 'deviceId',
                                    value: filters.deviceId,
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    onEnterKey: loadAuthorizations,
                                },
                                {
                                    caption: t('UnlockerCode'),
                                    type: 'text',
                                    name: 'unlockerCode',
                                    value: filters.unlockerCode,
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    onEnterKey: loadAuthorizations,
                                },
                                {
                                    caption: t('Fullname'),
                                    type: 'text',
                                    name: 'mshipFullname',
                                    value: filters.mshipFullname,
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    onEnterKey: loadAuthorizations,
                                },
                            ]}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setFilter({
                                    ...filters,
                                    [name]: value as string,
                                });
                            }}
                            onFilter={loadAuthorizations}
                        />

                        <SttTable
                            onFetch={loadAuthorizations}
                            data={authorizations}
                            perPage={30}
                            columns={[
                                {
                                    title: t('DeviceId'),
                                    value: (row: Authorization) => row.deviceId,
                                },
                                {
                                    title: t('Device'),
                                    value: (row: Authorization) =>
                                        row.deviceName,
                                },
                                {
                                    title: t('DeviceType'),
                                    value: (row: Authorization) => (
                                        <SttChipDeviceType
                                            types={
                                                row.deviceType
                                                    ? [
                                                          {
                                                              type: row.deviceType as DeviceType,
                                                          },
                                                      ]
                                                    : []
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: t('Module'),
                                    value: (row: Authorization) => (
                                        <SttChipModule module={row.module} />
                                    ),
                                },
                                {
                                    title: t('DateIni'),
                                    value: (row: Authorization) =>
                                        format(
                                            new Date(row.ini),
                                            'd-M-yyyy, H:mm',
                                        ),
                                },
                                {
                                    title: t('DateEnd'),
                                    value: (row: Authorization) =>
                                        format(
                                            new Date(row.end),
                                            'd-M-yyyy, H:mm',
                                        ),
                                },
                                {
                                    title: t('UnlockerCode'),
                                    value: (row: Authorization) =>
                                        row.unlockerCode,
                                },
                                {
                                    align: 'left',
                                    title: t('User'),
                                    value: (row: Authorization) =>
                                        row.mshipFullname,
                                },
                            ]}
                        />
                    </Paper>
                </Box>
            </Container>
        </SttFullDialog>
    );
}
